import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Card,
  Row,
  Col,
  Select,
  message,
  Modal,
  Typography,
  Tooltip,
  Form,
  DatePicker as AntDatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined
} from '@ant-design/icons';
import { adminAPI } from '../../utils/api';
import moment from 'moment';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ConsumerEditModal from './ConsumerEditModal';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const Consumers = () => {
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: undefined,
    start_date: null,
    end_date: null
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchConsumers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchConsumers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };

      // Clean up null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const response = await adminAPI.getConsumers(params);
      const { consumers: data, pagination: paginationData } = response.data;

      setConsumers(data);
      setPagination(prev => ({
        ...prev,
        total: paginationData.total_items
      }));
    } catch (error) {
      message.error('Failed to fetch consumers');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    if (Array.isArray(dates) && dates.length === 2) {
      let [start, end] = dates;
      
      // Auto-swap if user selects dates in reverse order
      if (start && end && moment(start).isAfter(moment(end))) {
        [start, end] = [end, start];
      }

      setFilters(prev => ({
        ...prev,
        start_date: start ? moment(start).format('YYYY-MM-DD') : null,
        end_date: end ? moment(end).format('YYYY-MM-DD') : null
      }));
      
      // Only trigger search if both dates are selected or both are cleared
      if ((start && end) || (!start && !end)) {
        setPagination(prev => ({ ...prev, current: 1 }));
      }
    } else {
      setFilters(prev => ({
        ...prev,
        start_date: null,
        end_date: null
      }));
      setPagination(prev => ({ ...prev, current: 1 }));
    }
  };

  const handleToggleStatus = async (consumerId, currentStatus) => {
    const isCurrentlyActive = currentStatus === 1 || currentStatus === true || currentStatus === '1';
    const newStatus = !isCurrentlyActive;

    // Optimistic UI update
    setConsumers(prev => prev.map(c => 
      c.id === consumerId ? { ...c, is_active: newStatus ? 1 : 0 } : c
    ));

    try {
      await adminAPI.updateConsumer(consumerId, {
        is_active: newStatus
      });
      message.success(`Consumer ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchConsumers();
    } catch (error) {
      // Revert on error
      setConsumers(prev => prev.map(c => 
        c.id === consumerId ? { ...c, is_active: currentStatus } : c
      ));
      message.error('Failed to update consumer status');
    }
  };

  const handleDelete = (consumer) => {
    Modal.confirm({
      title: 'Delete Consumer',
      content: `Are you sure you want to delete ${consumer.name}? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await adminAPI.deleteConsumer(consumer.id);
          message.success('Consumer deleted successfully');
          fetchConsumers();
        } catch (error) {
          message.error('Failed to delete consumer');
        }
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      is_active: undefined,
      start_date: null,
      end_date: null
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Consumer Modal Logic
  const [consumerModalVisible, setConsumerModalVisible] = useState(false);
  const [editingConsumer, setEditingConsumer] = useState(null);

  const showConsumerModal = (consumer = null) => {
    setEditingConsumer(consumer);
    setConsumerModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'center_consumer_id',
      key: 'center_consumer_id',
      width: 70,
      render: (id) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{id}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <Link to={`/consumers/${record.id}`} style={{ fontWeight: 500 }}>
            {text}
          </Link>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-',
    },
    {
      title: 'Coach',
      dataIndex: 'coach_name',
      key: 'coach_name',
      render: (coach) => coach || '-',
    },
    {
      title: 'Plan',
      dataIndex: 'plan_type',
      key: 'plan_type',
      render: (type) => {
        let color = 'gold';
        let label = 'Membership';
        if (type === 'sample') { color = 'purple'; label = 'Sample User'; }
        if (type === '3_day') { color = 'cyan'; label = '3 Day Plan'; }
        return <Tag color={color}>{label.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Center',
      dataIndex: 'center_name',
      key: 'center_name',
      render: (center) => center || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => {
        const isCurrentlyActive = isActive === 1 || isActive === true || isActive === '1';
        return (
          <Tag
            color={isCurrentlyActive ? 'success' : 'default'}
            onClick={() => handleToggleStatus(record.id, isCurrentlyActive)}
            style={{ cursor: 'pointer' }}
          >
            {isCurrentlyActive ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'Joined',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space className="table-actions">
          <Tooltip title="View Details">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/consumers/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showConsumerModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Consumers Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchConsumers}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showConsumerModal()}
            >
              Add Consumer
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by name or email"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker
              selected={filters.start_date ? moment(filters.start_date).toDate() : null}
              onChange={(dates) => {
                if (Array.isArray(dates)) {
                  // Range selected
                  if (dates[0] && dates[1]) {
                    handleDateRangeChange(dates);
                  } else if (dates[0]) {
                    // Only start date selected, keep end date if exists
                    const currentEnd = filters.end_date ? moment(filters.end_date).toDate() : null;
                    handleDateRangeChange([dates[0], currentEnd || dates[0]]);
                  } else {
                    // Cleared
                    handleDateRangeChange([null, null]);
                  }
                } else if (dates) {
                  // Single date clicked (start of new range)
                  handleDateRangeChange([dates, null]);
                } else {
                  // Cleared
                  handleDateRangeChange([null, null]);
                }
              }}
              startDate={filters.start_date ? moment(filters.start_date).toDate() : null}
              endDate={filters.end_date ? moment(filters.end_date).toDate() : null}
              selectsRange
              dateFormat="yyyy-MM-dd"
              placeholderText="Select date range"
              isClearable
              className="ant-input"
              style={{ width: '100%', height: 32 }}
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Button onClick={clearFilters}>Clear</Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={consumers}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} consumers`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      <ConsumerEditModal
        visible={consumerModalVisible}
        onCancel={() => setConsumerModalVisible(false)}
        onSuccess={() => {
          setConsumerModalVisible(false);
          setEditingConsumer(null);
          fetchConsumers();
        }}
        consumer={editingConsumer}
      />
    </div>
  );
};

export default Consumers;
